import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { qtyDisplay } from "@/lib/utils";
import { toast } from "sonner";
import { Loader2, RefreshCw, Trash2, X, Clock, CheckCircle2, AlertTriangle, Truck, Bot, Stethoscope, Package } from "lucide-react";

const STATUS_BADGE = {
  pending_payment: "bg-yellow-500/15 text-yellow-300 border-yellow-500/20",
  pending_vet_review: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/20",
  under_vet_review: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/20",
  vet_approved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
  vet_rejected: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/20",
  vet_modification_requested: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/20",
  sent_to_fulfillment: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/20",
  processing: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/20",
  shipped: "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/20",
  delivered: "bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/20",
  completed: "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/20",
  cancelled: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/20",
  refunded: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/20",
};

const AGENT_ICON = {
  system: Bot,
  rx_clarity_ai: Bot,
  license_validator_ai: Bot,
  estimator_ai: Bot,
  router: Package,
  vet: Stethoscope,
  fulfillment: Truck,
};

const AGENT_LABEL = {
  system: "System",
  rx_clarity_ai: "AI · Rx Clarity",
  license_validator_ai: "AI · License",
  estimator_ai: "AI · Estimator",
  router: "Router",
  vet: "Vet",
  fulfillment: "Fulfillment",
};

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function Timeline({ entries }) {
  if (!entries || entries.length === 0) {
    return <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40">No events recorded yet.</p>;
  }
  return (
    <ol className="relative border-l border-paw-border dark:border-white/10 ml-3 space-y-4" data-testid="order-timeline">
      {entries.map((e, i) => {
        const Icon = AGENT_ICON[e.agent] || Clock;
        const isAlert = (e.event || "").includes("rejected") || (e.event || "").includes("failed") || (e.event || "").includes("issue");
        return (
          <li key={i} className="ml-5" data-testid={`timeline-event-${i}`}>
            <span className={`absolute -left-[11px] flex items-center justify-center w-[22px] h-[22px] rounded-full ring-4 ring-white dark:ring-[#0e1812] ${isAlert ? "bg-red-500/30 text-red-700 dark:text-red-200" : "bg-emerald-500/20 text-emerald-700 dark:text-emerald-200"}`}>
              <Icon className="w-3 h-3" />
            </span>
            <div className="flex flex-wrap items-baseline gap-2 mb-0.5">
              <p className="font-outfit font-semibold text-sm text-paw-text dark:text-white">{AGENT_LABEL[e.agent] || e.agent}</p>
              <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">{fmt(e.at)}</p>
            </div>
            <p className="font-manrope text-sm text-paw-text-secondary dark:text-white/70">{e.message}</p>
          </li>
        );
      })}
    </ol>
  );
}

function OrderDetailDrawer({ order, onClose, vets, centers, onAssignVet, onAssignCenter }) {
  if (!order) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose} data-testid="order-detail-drawer">
      <div className="w-full max-w-2xl bg-emerald-50/60 dark:bg-[#0e1812] border-l border-paw-border dark:border-white/10 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-paw-border dark:border-white/5 sticky top-0 bg-emerald-50/60 dark:bg-[#0e1812] z-10">
          <div>
            <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40">Order</p>
            <h2 className="font-outfit font-bold text-xl">{order.order_number || order.order_id?.slice(0, 8)}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/[0.03] dark:hover:bg-white/5 rounded-lg" data-testid="order-detail-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status + assignments */}
          <div className="bg-emerald-50 dark:bg-[#142318] border border-paw-border dark:border-white/5 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-md border text-xs font-manrope font-medium ${STATUS_BADGE[order.status] || "bg-black/[0.03] dark:bg-white/5 text-paw-text-muted dark:text-white/40 border-paw-border dark:border-white/10"}`}>{order.status}</span>
                {order.insurance_partner_name && <span className="inline-flex items-center px-2.5 py-1 rounded-md border text-xs font-manrope font-medium bg-paw-accent/15 text-paw-accent border-paw-accent/20">Insurance · {order.insurance_partner_name}</span>}
              </div>
              <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Created {fmt(order.created_at)}</p>
            </div>
            <p className="font-manrope text-xs text-emerald-300/70 mb-3">⚡ Routing is automated. Use these only to override the auto-assigned vet or fulfillment centre.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-manrope text-xs text-paw-text-muted dark:text-white/40 uppercase tracking-wider mb-1.5 block">Assigned Vet (auto)</label>
                <select onChange={(e) => e.target.value && onAssignVet(order.order_id, e.target.value)} value={order.assigned_vet_id || ""} className="w-full h-9 px-2 rounded-md bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-xs font-manrope" data-testid="drawer-assign-vet">
                  <option value="">Unassigned</option>
                  {vets.map((v) => <option key={v.id || v.email} value={v.id || v.email}>{v.first_name} {v.last_name}</option>)}
                </select>
              </div>
              <div>
                <label className="font-manrope text-xs text-paw-text-muted dark:text-white/40 uppercase tracking-wider mb-1.5 block">Fulfillment Centre (auto)</label>
                <select onChange={(e) => e.target.value && onAssignCenter(order.order_id, e.target.value)} value={order.fulfillment_center_id || ""} className="w-full h-9 px-2 rounded-md bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-xs font-manrope" data-testid="drawer-assign-center">
                  <option value="">Unassigned</option>
                  {centers.map((c) => <option key={c.center_id} value={c.center_id}>{c.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Customer + pet + meds */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-emerald-50 dark:bg-[#142318] border border-paw-border dark:border-white/5 rounded-2xl p-5">
              <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Customer</p>
              <p className="font-manrope text-sm text-paw-text dark:text-white">{order.customer_name || "n/a"}</p>
              <p className="font-manrope text-xs text-paw-text-muted dark:text-white/50">{order.customer_email || "n/a"}</p>
            </div>
            <div className="bg-emerald-50 dark:bg-[#142318] border border-paw-border dark:border-white/5 rounded-2xl p-5">
              <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Pet</p>
              <p className="font-manrope text-sm text-paw-text dark:text-white">{order.pet?.name || "n/a"}</p>
              <p className="font-manrope text-xs text-paw-text-muted dark:text-white/50">{order.pet?.species || "n/a"}</p>
            </div>
            <div className="bg-emerald-50 dark:bg-[#142318] border border-paw-border dark:border-white/5 rounded-2xl p-5 col-span-2">
              <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Medication</p>
              <p className="font-outfit font-semibold text-base text-paw-text dark:text-white">{order.medication?.name || "n/a"}</p>
              <p className="font-manrope text-xs text-paw-text-muted dark:text-white/50 mt-1">{order.medication?.dosage} · Quantity {qtyDisplay(order.medication)}</p>
              {order.payment?.amount > 0 && <p className="font-manrope text-xs text-emerald-300/80 mt-2">Paid: ${Number(order.payment.amount).toFixed(2)}</p>}
            </div>
          </div>

          {/* Audit timeline */}
          <div className="bg-emerald-50 dark:bg-[#142318] border border-paw-border dark:border-white/5 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
              <p className="font-outfit font-semibold text-sm">Audit Timeline</p>
            </div>
            <Timeline entries={order.timeline || []} />
          </div>

          {order.vet_review && (
            <div className="bg-emerald-50 dark:bg-[#142318] border border-paw-border dark:border-white/5 rounded-2xl p-5">
              <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Vet Decision</p>
              <p className="font-manrope text-sm text-paw-text dark:text-white">
                <Stethoscope className="w-3 h-3 inline mr-1.5" />
                {order.vet_review.vet_name} · <span className="text-emerald-700 dark:text-emerald-300">{order.vet_review.decision}</span>
              </p>
              {order.vet_review.signature && <img src={order.vet_review.signature} alt="signature" className="bg-white rounded p-1 mt-2 max-h-16" />}
              {order.vet_review.notes && <p className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 mt-2">"{order.vet_review.notes}"</p>}
            </div>
          )}

          {order.tracking?.number && (
            <div className="bg-emerald-50 dark:bg-[#142318] border border-paw-border dark:border-white/5 rounded-2xl p-5">
              <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Tracking</p>
              <p className="font-manrope text-sm text-paw-text dark:text-white">{order.tracking.carrier} · {order.tracking.number}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vets, setVets] = useState([]);
  const [centers, setCenters] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [o, u, c] = await Promise.all([
        api.get("/admin/orders"),
        api.get("/admin/users"),
        api.get("/admin/fulfillment-centers"),
      ]);
      setOrders(o.data || []);
      setVets((u.data || []).filter((x) => x.role === "vet"));
      setCenters(c.data || []);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openOrder = async (orderId) => {
    try {
      const { data } = await api.get(`/admin/orders/${orderId}`);
      setSelected(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const assignVet = async (orderId, vetId) => {
    try { await api.patch(`/admin/orders/${orderId}/assign-vet`, { vet_id: vetId }); toast.success("Vet assigned"); load(); openOrder(orderId); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const assignCenter = async (orderId, centerId) => {
    try { await api.patch(`/admin/orders/${orderId}/assign-fulfillment`, { center_id: centerId }); toast.success("Fulfillment assigned"); load(); openOrder(orderId); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeOrder = async (orderId, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this order? This cannot be undone.")) return;
    try { await api.delete(`/admin/orders/${orderId}`); toast.success("Order deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-7xl" data-testid="admin-orders">
      <div className="flex items-end justify-between mb-6 sm:mb-10">
        <div>
          <p className="font-manrope text-[11px] uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-1.5">Pipeline</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Orders</h1>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-3 sm:px-4 h-9 sm:h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 font-manrope text-xs sm:text-sm transition-colors" data-testid="orders-refresh">
          <RefreshCw className="w-4 h-4" /> <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : orders.length === 0 ? (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-12 text-center font-manrope text-paw-text-muted dark:text-white/40">No orders yet.</div>
      ) : (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl overflow-x-auto">
          <table className="w-full">
            <thead className="bg-black/[0.02] dark:bg-white/[0.02] border-b border-paw-border dark:border-white/5">
              <tr className="text-left">
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Order #</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Customer</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Pet</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Medication</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Status</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.order_id} onClick={() => openOrder(o.order_id)} className="border-b border-paw-border dark:border-white/5 last:border-0 hover:bg-black/[0.03] dark:hover:bg-white/[0.04] cursor-pointer" data-testid={`order-row-${o.order_id}`}>
                  <td className="px-5 py-4 font-manrope text-xs text-paw-text-secondary dark:text-white/60">{o.order_number || `${o.order_id?.slice(0, 8)}…`}</td>
                  <td className="px-5 py-4 font-manrope text-sm">{o.customer_name || "n/a"} <span className="block text-xs text-paw-text-muted dark:text-white/40">{o.customer_email}</span>{o.insurance_partner_name && <span className="inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-paw-accent/15 text-paw-accent">Insurance · {o.insurance_partner_name}</span>}</td>
                  <td className="px-5 py-4 font-manrope text-sm">{o.pet?.name || "n/a"} <span className="text-paw-text-muted dark:text-white/40">({o.pet?.species || "?"})</span></td>
                  <td className="px-5 py-4 font-manrope text-sm">{o.medication?.name || "n/a"}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md border text-xs font-manrope font-medium ${STATUS_BADGE[o.status] || "bg-black/[0.03] dark:bg-white/5 text-paw-text-muted dark:text-white/40 border-paw-border dark:border-white/10"}`}>{o.status}</span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={(e) => removeOrder(o.order_id, e)} className="p-2 hover:bg-red-500/10 rounded-md transition-colors" title="Delete order" data-testid={`order-delete-${o.order_id}`}>
                      <Trash2 className="w-4 h-4 text-red-700 dark:text-red-400" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <OrderDetailDrawer order={selected} onClose={() => setSelected(null)} vets={vets} centers={centers} onAssignVet={assignVet} onAssignCenter={assignCenter} />
    </div>
  );
}
